import { defineStore } from 'pinia';
import { GET_REGISTRY_HOME_TAB, GET_VISIBILITY_STATUS, UPDATE_HEADER_BANNER_VALUES } from '~/types/actions';
import {
  SET_REGISTRY_HOME_TAB,
  SET_HEADER_BANNER_ERROR,
  SET_REGISTRANT_HEADER_BANNER_LOADING,
  SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR,
  SET_HEADER_BANNER_API_ERROR_RESPONSE,
  SET_HEADER_BANNER_ERROR_DESCRIPTION,
  UPDATE_GIFTS_PURCHASED,
  UPDATE_PRIVACY_PREFERENCE_LOADER,
  SET_PRIVACY_PREFERENCE_API_TIMEOUT,
  SET_GIFT_CARD_OPTIONS,
} from '~/types/mutations';
import { SoftSignInValidation } from '~/utils/SoftSignInValidation.js';
import { httpGetProtectedAPIHeaders, httpUpdateAPIHeaders } from '~/utils/APIUtil';
import { generateLogin365FingerPrint } from '~/utils/PersistentLoginUtil';
import $http from '@vue-util/http';
import { useEnvPropsStore } from '~/stores/envProps';

const GET_VISIBILITY_STATUS_ENDPOINT = '/regsvc/registry/api/v1/registry';
const REGISTRY_HOME_TAB_ENDPOINT = '/regsvc/registry/api/v2/registry/getRegistry';

export const useHeaderBannerStore = defineStore('headerBanner', {
  state: () => ({
    headerBanner: {},
    registrantHeaderBannerLoading: true,
    error: false,
    shareRegistryVisibilityAPiError: null,
    headerBannerApiErrorResponse: null,
    errorDescription: '',
    privacyPreferenceLoader: false,
    privacyPreferenceApiTimeout: false,
  }),
  actions: {
    [SET_REGISTRY_HOME_TAB](headerBanner) {
      this.headerBanner = headerBanner;
    },
    [SET_HEADER_BANNER_ERROR](error) {
      this.error = error;
    },
    [SET_REGISTRANT_HEADER_BANNER_LOADING](registrantHeaderBannerLoading) {
      this.registrantHeaderBannerLoading = registrantHeaderBannerLoading;
    },
    [SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR](errorPayload) {
      this.shareRegistryVisibilityAPiError = errorPayload;
    },
    [SET_HEADER_BANNER_API_ERROR_RESPONSE](error) {
      this.headerBannerApiErrorResponse = error;
    },
    [SET_HEADER_BANNER_ERROR_DESCRIPTION](errorDescription) {
      this.errorDescription = errorDescription;
    },
    [UPDATE_GIFTS_PURCHASED](updateGiftsPurchased) {
      this.headerBanner.giftsPurchased += updateGiftsPurchased;
    },
    [UPDATE_PRIVACY_PREFERENCE_LOADER](value) {
      this.headerBanner.privacyPreferenceLoader = value;
    },
    [SET_PRIVACY_PREFERENCE_API_TIMEOUT](value) {
      this.privacyPreferenceApiTimeout = value;
    },
    [SET_GIFT_CARD_OPTIONS](data) {
      this.headerBanner.physicalGiftCardEnabled = data.physicalGiftCardEnabled;
      this.headerBanner.egiftCardEnabled = data.egiftCardEnabled;
    },
    async [GET_REGISTRY_HOME_TAB](params) {
      const envPropsStore = useEnvPropsStore();

      this[SET_REGISTRANT_HEADER_BANNER_LOADING](true);
      const httpSource = $http.CancelToken.source();
      const timer = setTimeout(() => {
        this[SET_REGISTRY_HOME_TAB]({});
        this[SET_HEADER_BANNER_ERROR](true);
        this[SET_REGISTRANT_HEADER_BANNER_LOADING](false);
        httpSource.cancel();
      }, envPropsStore.envProps?.apiTimeout || 0);
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.get(`${REGISTRY_HOME_TAB_ENDPOINT}?orgId=${params.orgId}&registryGUID=${params.registryGUID}`, {
          headers: httpGetProtectedAPIHeaders(login365FingerPrint),
          cancelToken: httpSource.token,
        });
        clearTimeout(timer);
        const isError = response?.data?.errors || response?.data?.error;
        if (response.data?.registryInfo) {
          this[SET_REGISTRY_HOME_TAB](response.data?.registryInfo);
          this[SET_HEADER_BANNER_ERROR](false);
        } else {
          this[SET_REGISTRY_HOME_TAB]({});
          this[SET_HEADER_BANNER_ERROR](true);
          this[SET_HEADER_BANNER_API_ERROR_RESPONSE]({ response });
          this[SET_HEADER_BANNER_ERROR_DESCRIPTION](isError?.[0]?.description);
        }
      } catch (error) {
        SoftSignInValidation(error);
        clearTimeout(timer);
        this[SET_REGISTRY_HOME_TAB]({});
        this[SET_HEADER_BANNER_ERROR_DESCRIPTION]('');
        this[SET_HEADER_BANNER_ERROR](true);
        this[SET_HEADER_BANNER_API_ERROR_RESPONSE](error);
      } finally {
        this[SET_REGISTRANT_HEADER_BANNER_LOADING](false);
      }
    },
    async [UPDATE_HEADER_BANNER_VALUES](params) {
      const httpSource = $http.CancelToken.source();
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.get(`${REGISTRY_HOME_TAB_ENDPOINT}?orgId=${params.orgId}&registryGUID=${params.registryGUID}`, {
          headers: httpGetProtectedAPIHeaders(login365FingerPrint),
          cancelToken: httpSource.token,
        });
        const isError = response?.data?.errors || response?.data?.error;
        if (response.data?.registryInfo) {
          this[SET_REGISTRY_HOME_TAB](response.data?.registryInfo);
          this[SET_HEADER_BANNER_ERROR](false);
        } else {
          this[SET_HEADER_BANNER_API_ERROR_RESPONSE]({ response });
          this[SET_HEADER_BANNER_ERROR_DESCRIPTION](isError?.[0]?.description);
        }
      } catch (error) {
        this[SET_HEADER_BANNER_ERROR_DESCRIPTION]('');
        this[SET_HEADER_BANNER_API_ERROR_RESPONSE](error);
      }
    },
    async [GET_VISIBILITY_STATUS](payload) {
      const envPropsStore = useEnvPropsStore();

      this[SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR](null);
      this[UPDATE_PRIVACY_PREFERENCE_LOADER](true);
      const httpSource = $http.CancelToken.source();
      const timer = setTimeout(() => {
        this[UPDATE_PRIVACY_PREFERENCE_LOADER](false);
        this[SET_PRIVACY_PREFERENCE_API_TIMEOUT](true);
        httpSource.cancel();
      }, envPropsStore.envProps?.apiTimeout || 0);
      try {
        const login365FingerPrint = await generateLogin365FingerPrint();
        const response = await $http.put(`${GET_VISIBILITY_STATUS_ENDPOINT}`, payload, {
          headers: httpUpdateAPIHeaders(payload, login365FingerPrint),
        });
        clearTimeout(timer);
        const isError = response?.data?.errors || response?.data?.error;
        if (isError) {
          this[SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR](isError?.[0]);
        } else {
          this.headerBanner.privacyPreference = payload.basicInfoUpdate.privacyPreference;
          this[UPDATE_PRIVACY_PREFERENCE_LOADER](false);
          this[SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR](null);
        }
      } catch (error) {
        clearTimeout(timer);
        SoftSignInValidation(error);
        this[SET_SHARE_REGISTRY_VISIBILITY_STATUS_API_ERROR](error?.response?.data?.errors?.[0]);
      }
    },
  },
});
